import React, {
  Component,
  CSSProperties,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

interface CheckBoxProps {
  id?: string;
  className?: string;
  isChecked: boolean;
  readonly?: boolean;
  onToggle?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CheckBox: FC<CheckBoxProps> = (props) => {
  return (
    <label className={classNames('checkbox', props.className)}>
      <input
        id={props.id || undefined}
        checked={!!props.isChecked}
        onChange={props.onToggle}
        type='checkbox'
        readOnly={props.readonly ?? false}
      />
      <span className='checkbox__check'></span>
      <span>{props.children}</span>
    </label>
  );
};

export interface ButtonProps {
  /** @default 'outlined' */
  variant?: 'contained' | 'outlined' | 'text' | 'link';
  /** @default 'info' */
  color?: 'info' | 'success' | 'error' | 'warning';
  /** @default 'small' */
  size?: 'small' | 'large';
  /** @default false */
  disabled?: boolean;
  /**
   * When isLoading is true,
   * button will be disabled
   * and a loader will appear as endIcon on button
   * @default false
   */
  isLoading?: boolean;
  /**
   * If a promise is returned then
   * button will be disabled
   * and a loader will appear as endIcon on button
   * until the promise is resolved or rejected
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void | Promise<any>;
  children: ReactNode;
  className?: string;
  /** @default 'button' */
  type?: 'button' | 'submit';
  /** @default false */
  fullWidth?: boolean;
  /** @default false */
  preventHoverStyles?: boolean;
  /** @default false */
  preventMinWidth?: boolean;
}

export const Button = ({
  variant = 'contained',
  color = 'info',
  size = 'small',
  disabled = false,
  isLoading: _isLoading = false,
  onClick,
  children,
  className,
  type = 'button',
  fullWidth,
  preventHoverStyles,
  preventMinWidth,
}: ButtonProps) => {
  const [isAwaiting, setIsAwaiting] = useState(false);

  const isLoading = useMemo(() => _isLoading || isAwaiting, [_isLoading, isAwaiting]);
  const isDisabled = useMemo(() => disabled || isLoading, [disabled, isLoading]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const ripple = document.createElement('span');
    ripple.classList.add('ripple');
    ripple.style.top = event.nativeEvent.offsetY + 'px';
    ripple.style.left = event.nativeEvent.offsetX + 'px';
    buttonRef.current?.append(ripple);

    // Remove ripple after animation
    setTimeout(() => ripple.remove(), 300);

    // --- CASE 1: No onClick, type="submit" → allow native submit ---
    if (!onClick && type === 'submit') {
      return; // do NOT block or await → browser will submit the form
    }

    // --- CASE 2: onClick exists → run async logic ---
    (async () => {
      try {
        setIsAwaiting(true);
        await new Promise((resolve) => setTimeout(resolve, 300));
        await onClick?.(event);
      } finally {
        setIsAwaiting(false);
      }
    })();
  };

  return (
    <button
      ref={buttonRef}
      className={classNames(
        'btn',
        `btn-${variant}`,
        `btn-${color}`,
        `btn-${size}`,
        {
          'btn-disabled': disabled,
          'btn-full-width': fullWidth,
          'hover-prevented': preventHoverStyles,
          'min-width-prevented': preventMinWidth,
        },
        className
      )}
      disabled={isDisabled}
      onClick={handleClick}
      type={type}
    >
      {children}
      {size !== 'small' && isLoading && <LoaderSpinner />}
    </button>
  );
};

interface TooltipProps {
  label: string;
  charLimit: number;
}

interface TooltipState {
  ishovered: boolean;
}

export class Tooltip extends Component<TooltipProps, TooltipState> {
  private tooltip: RefObject<any>;

  constructor(props: TooltipProps) {
    super(props);
    this.state = {
      ishovered: false,
    };
    this.tooltip = React.createRef();
    this.mouseenter = this.mouseenter.bind(this);
    this.mouseleave = this.mouseleave.bind(this);
  }

  mouseleave = () => {
    this.setState({ ishovered: false });
  };

  mouseenter = () => {
    this.setState({ ishovered: true });
  };

  render() {
    const { label, charLimit } = this.props;

    return (
      <div className='tooltip__wrap'>
        <p onMouseEnter={this.mouseenter} onMouseLeave={this.mouseleave}>
          {this.props.children
            ? this.props.children
            : label.length <= charLimit
            ? label
            : label.substr(0, charLimit) + '...'}
        </p>
        {label.length > charLimit ? (
          <span className={`tooltip ${this.state.ishovered ? 'show' : ''}`}>{label}</span>
        ) : null}
      </div>
    );
  }
}

export const LoaderSpinner: FC<{ style?: CSSProperties }> = ({ style }) => {
  return <div className='loader-spinner' style={style} />;
};

export const Loader: FC = () => {
  return (
    <div className='loader'>
      <LoaderSpinner />
    </div>
  );
};

interface TextFieldProps {
  placeholder?: string;
  selector?: string;
  wrapperClassName?: string;
  type?: string;
  style?: CSSProperties;
  min?: string | number;
  max?: string | number;
  value?: string | number | string[];
  hasError?: boolean;
  maxLength?: number;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onchange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onkeydown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      placeholder,
      selector,
      wrapperClassName,
      type,
      style,
      min,
      max,
      value,
      onchange,
      hasError,
      onkeydown,
      maxLength,
      onFocus,
    },
    ref
  ) => {
    return (
      <div className={classNames('text-field__wrapper', wrapperClassName)} style={style}>
        <input
          ref={ref}
          className={classNames('text-field__input', selector, {
            'has-error': hasError,
          })}
          placeholder={placeholder}
          type={type}
          min={min}
          max={max}
          onChange={onchange}
          onKeyDown={onkeydown}
          value={value}
          maxLength={maxLength}
          onFocus={onFocus}
        />
        <div className='text-field__border' />
      </div>
    );
  }
);

/**
 * Sets caret position inside input elements.
 * This does not work inside focus event in safari.
 * @see https://stackoverflow.com/a/512542/9709887
 * @param elem input dom reference
 * @param caretPos caret position from left, start with 0
 */
function setCaretPosition(elem: HTMLInputElement | undefined | null, caretPos: number) {
  if (elem != null) {
    if ((elem as any).createTextRange) {
      const range = (elem as any).createTextRange();
      range.move('character', caretPos);
      range.select();
    } else {
      if (elem.selectionStart) {
        elem.focus();
        elem.setSelectionRange(caretPos, caretPos);
      } else {
        elem.focus();
      }
    }
  }
}

interface PercentInputProps extends Omit<TextFieldProps, 'onchange'> {
  isRequired?: boolean;
  initValue?: number | undefined;
  onchange(v: number): void;
}

export const PercentInput: FC<PercentInputProps> = ({ initValue, onchange, isRequired, ...props }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState((initValue || 0).toString());
  useEffect(() => setValue((initValue || 0).toString()), [initValue]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, _prev: string) => {
      const rawValue = e.target.value;
      const input = rawValue.replace('%', '');
      const value = parseFloat(input);

      if (isNaN(value) || value < 0) {
        onchange(0);
        setValue('0');
        setTimeout(() => setCaretPosition(ref.current, 1));
        return;
      }

      if (value > 100) {
        onchange(100);
        setValue('100');
        setTimeout(() => setCaretPosition(ref.current, 3));
        return;
      }

      onchange(value);
      setValue(input.slice(-1) === '.' ? input : value.toString());

      if (value < 10) {
        setTimeout(() => setCaretPosition(ref.current, 1));
      }
    },
    [onchange]
  );

  return (
    <TextField
      {...props}
      ref={ref}
      type='text'
      hasError={props.hasError || (isRequired && !value)}
      value={`${value}%`}
      onchange={(e) => onChange(e, value)}
    />
  );
};

// export const TextField = (props: Props): View =>
//   h(TextFieldC, <any>props);
